import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Clock, Users, Activity } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface StreamAnalyticsProps {
  streamId?: string;
  viewerData?: Array<{ time: string; viewers: number }>;
  retentionData?: Array<{ minute: number; percentage: number }>;
  peakViewers?: number;
  peakTime?: string;
  avgViewDuration?: number;
}

export const StreamAnalytics = ({ 
  viewerData = [],
  retentionData = [],
  peakViewers = 0,
  peakTime = 'N/A',
  avgViewDuration = 0
}: StreamAnalyticsProps) => {
  // Generate mock data if none provided
  const defaultViewerData = viewerData.length > 0 ? viewerData : [
    { time: '0:00', viewers: 10 },
    { time: '0:15', viewers: 25 },
    { time: '0:30', viewers: 45 },
    { time: '0:45', viewers: 38 },
    { time: '1:00', viewers: 52 },
    { time: '1:15', viewers: 48 },
  ];

  const defaultRetentionData = retentionData.length > 0 ? retentionData : [
    { minute: 0, percentage: 100 },
    { minute: 5, percentage: 85 },
    { minute: 10, percentage: 72 },
    { minute: 15, percentage: 65 },
    { minute: 20, percentage: 58 },
    { minute: 25, percentage: 52 },
    { minute: 30, percentage: 48 },
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Peak Viewers</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{peakViewers || 52}</div>
            <p className="text-xs text-muted-foreground mt-1">
              at {peakTime || '1:00'}
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg. View Duration</CardTitle>
            <Clock className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgViewDuration || 18}m</div>
            <p className="text-xs text-muted-foreground mt-1">
              per viewer
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Retention Rate</CardTitle>
            <Activity className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">48%</div>
            <p className="text-xs text-muted-foreground mt-1">
              at 30 minutes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Viewer Count Over Time */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Viewer Count Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={defaultViewerData}>
              <defs>
                <linearGradient id="viewerGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis 
                dataKey="time" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="viewers" 
                stroke="hsl(var(--primary))" 
                fillOpacity={1} 
                fill="url(#viewerGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Viewer Retention */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Viewer Retention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={defaultRetentionData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis 
                dataKey="minute" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                label={{ value: 'Minutes', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                label={{ value: '%', position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value) => `${value}%`}
              />
              <Line 
                type="monotone" 
                dataKey="percentage" 
                stroke="hsl(var(--secondary))" 
                strokeWidth={3}
                dot={{ fill: 'hsl(var(--secondary))', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
